import React from "react";
import { cn } from "@/lib/utils.ts";

interface GradientProgressBarProps {
  value: number;
  min?: number;
  max?: number;
  className?: string;
}

export function GradientProgressBar({
  value,
  min = 0,
  max = 100,
  className,
}: GradientProgressBarProps) {
  // Normalize value between 0 and 100
  const normalizedValue = ((value - min) / (max - min)) * 100;
  const clampedValue = Math.min(Math.max(normalizedValue, 0), 100);

  // Get color based on value ranges
  const getStateColor = (value: number) => {
    if (value < 33) {
      return "#B2182C";
    }
    if (value < 66) {
      return "#FFA028";
    }
    return "#3CA64B";
  };

  const currentColor = getStateColor(clampedValue);

  // Get status text based on value
  const getStatusText = (value: number) => {
    if (value < 33) {
      return `${value?.toFixed(0)}%`;
    }
    if (value < 66) {
      return `${value?.toFixed(0)}%`;
    }
    return `${value?.toFixed(0)}%`;
  };

  // Position the label based on value
  const getLabelPosition = (value: number) => {
    if (value >= 85) {
      return { right: "10px", transform: "none" };
    }
    return {
      left: `${value}%`,
      transform: "translateX(8px)",
    };
  };

  const labelStyle = getLabelPosition(clampedValue);

  return (
    <div className={cn("w-full h-2 relative", className)}>
      <div className="absolute top-0 left-0 w-full h-full flex items-start justify-center z-10 mt-3 bg-black/50">
        <span className="text-[12px] text-white font-medium leading-none">
          {getStatusText(clampedValue)}
        </span>
      </div>
      {/* Background bar */}
      <div className="w-full h-full rounded-full bg-gray-700 overflow-hidden">
        {/* Progress fill */}
        <div
          className="h-full rounded-full transition-all duration-300 relative"
          style={{
            width: `${clampedValue}%`,
            backgroundColor: currentColor,
          }}
        />
      </div>
      {/* Position indicator */}
      <div
        className="absolute top-1/2 w-1 h-4 bg-white rounded-full shadow-sm"
        style={{
          left: `${clampedValue}%`,
          transform: "translate(-50%, -50%)",
        }}
      />
    </div>
  );
}
